import { bound } from "@frui.ts/helpers";
import { ScreenBase } from "@frui.ts/screens/dist";
import { interfaces } from "inversify";
import { computed, observable, toJS } from "mobx";
import Vehicle from "models/vehicle";
import VehicleChangeDuringValidityRequest from "models/vehicleChangeDuringValidityRequest";
import EnumService from "services/enumService";
import LocalizationService from "services/localizationService";
import { czechID } from "config";
import LicensePlateChange from "../../models/licensePlateChange";
import OrderItem, { CountryIdValidationRules, LicensePlateValidationRules } from "../../models/orderItem";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import ChargeOrderLicenseItem from "models/chargeOrderLicenseItem";
import CountryResponse from "entities/countryResponse";
import ChargeOrderItem from "models/chargeOrderItem";
import VehicleRegistrationResponse from "entities/vehicleRegistrationResponse";

export interface VehicleInfoConfig {
  attachValidator: boolean;
  fixedLicensePlateSize?: boolean;
  maxLicensePlateSize?: number;
  context?: "new" | "old";
  countryIDConfirmation?: boolean;
  licensePlateConfirmation?: boolean;
  breakpoint?: "xl" | "xxl";
  loadCountriesOnInit?: boolean;
  initByLocalization?: boolean;
}

export default class VehicleInfoViewModel extends ScreenBase {
  @observable countries: any[] = [];
  loadCountriesOnInit = false;

  constructor(
    public item:
      | OrderItem
      | Vehicle
      | LicensePlateChange
      | VehicleChangeDuringValidityRequest
      | ChargeOrderLicenseItem
      | VehicleRegistrationResponse,
    public localizationService: LocalizationService,
    private enumService: EnumService,
    public readonly config: VehicleInfoConfig = { attachValidator: true },
    public showCountryWarning = false
  ) {
    super();

    // TODO Odstranit attach validátoru?
    if (this.config.attachValidator) {
      attachAutomaticBlurValidator(this.item, { ...LicensePlateValidationRules, ...CountryIdValidationRules });
    }

    if (config.loadCountriesOnInit || (this.item.countryId && this.item.countryId !== czechID)) {
      this.loadCountriesOnInit = true;
    }
    if (item instanceof ChargeOrderLicenseItem) {
      this.showCountryWarning = item._showCountryWarning;
    }
  }
  protected async onInitialize() {
    if (this.item.countryId === czechID && this.config.initByLocalization && this.localizationService.languageCode !== "CS") {
      this.item.countryId = undefined;
    }
    return super.onInitialize();
  }

  @computed
  get countryCode() {
    let countryCode = this.countries.find((x) => x.id == this.item.countryId)?.code;

    if (this.config.initByLocalization && this.localizationService.languageCode !== "CS") {
      return countryCode;
    }

    if (!countryCode && this.item.countryId === czechID) {
      countryCode = "CZ";
    }

    return countryCode;
  }

  @computed
  get isDisabled() {
    return this.config.context === "old";
  }

  @computed
  get defaultCountries() {
    if (this.enumService.isEnumLoaded("countries")) {
      return this.enumService.values("countries").map((item) => ({
        id: `${item.id}`,
        label: `${item.code} – ${item.name}`,
      }));
    } else {
      return [
        {
          id: czechID,
          label: `CZ – ${this.localizationService.translateAttribute("country", "CZ")}`,
        },
      ];
    }
  }

  deactivate(close: boolean): Promise<void> {
    return super.deactivate(close);
  }

  @bound
  async loadCountries(inputValue: string) {
    if (!this.countries.length) {
      this.countries = (await this.enumService.lazyValues("countries")).map((item: CountryResponse) => ({
        id: `${item.id}`,
        code: `${item.code}`,
        label: item.code + " – " + item.name,
      }));
    }

    return toJS(this.countries);
  }

  @computed
  get oldCountry() {
    return (this.item && this.defaultCountries.find((country) => country.id === this.item.countryId)?.label) || "";
  }

  @computed
  get oldLicensePlate() {
    if (this.item instanceof VehicleChangeDuringValidityRequest) {
      return this.item._originalLicensePlate;
    }
  }

  static Factory({ container }: interfaces.Context) {
    return (
      item:
        | OrderItem
        | Vehicle
        | LicensePlateChange
        | VehicleChangeDuringValidityRequest
        | ChargeOrderLicenseItem
        | ChargeOrderItem
        | VehicleRegistrationResponse,
      config: VehicleInfoConfig = { attachValidator: true, fixedLicensePlateSize: false }
    ) => {
      return new VehicleInfoViewModel(item, container.get(LocalizationService), container.get(EnumService), config);
    };
  }
}
